import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Target, ArrowRight, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { money } from "@/lib/utils";
import { apiEnrichScan } from "@/lib/api";
import AdvancedStrip from "./AdvancedStrip";
import { negotiationAdvanced } from "@/lib/advanced";

/**
 * Negotiation cheat sheet — auto-loads Claude Haiku 4.5 enrichment on first
 * scroll-into-view / mount if the neg data is empty. Cached server-side per scan.
 */
export default function Negotiation({ neg, scanId, report, plan = "free", onUpgrade }) {
  const initialHas = neg && (neg.auto_accept || neg.quick_reply);
  const [local, setLocal] = useState(initialHas ? neg : null);
  const [loading, setLoading] = useState(false);
  const [negError, setNegError] = useState(null);
  const unlockedAdvanced = plan === "reseller" || plan === "pro";
  const negAdv = negotiationAdvanced(report);

  useEffect(() => {
    // negError guards against infinite retry: once a request fails, wait
    // for an explicit retry click rather than looping every re-render.
    if (local || loading || !scanId || negError) return;
    setLoading(true);
    apiEnrichScan(scanId, "negotiation")
      .then((r) => { setLocal(r.negotiation || {}); setNegError(null); })
      .catch((e) => {
        const m = e?.jobError || e?.message || "generation_failed";
        setNegError(m === "generation_timeout"
          ? "Negotiation copy timed out. Please retry."
          : "Couldn't generate negotiation copy — please retry.");
      })
      .finally(() => setLoading(false));
  }, [local, loading, scanId, negError]);

  const retryNeg = () => setNegError(null);

  const copyReply = async () => {
    const txt = local?.quick_reply || "";
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(txt);
      } else {
        const ta = document.createElement("textarea");
        ta.value = txt;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Reply copied");
    } catch { toast.success("Reply copied"); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="glass p-4 sm:p-6"
      data-testid="negotiation-card"
    >
      <div className="flex items-center gap-2 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-mono-num text-[#F59E0B]">
        <Target size={12} /> Smart Negotiation Cheat Sheet
      </div>
      <h3 className="font-display text-lg sm:text-xl font-semibold text-white mt-1 mb-4 sm:mb-5">
        Close the deal without leaving margin on the table.
      </h3>

      {loading ? (
        <div className="flex items-center gap-2 text-[#64748B] text-xs sm:text-sm py-6">
          <Loader2 size={14} className="animate-spin" /> Generating your listing…
        </div>
      ) : negError ? (
        <div className="flex flex-col gap-2 py-6" data-testid="neg-enrich-error">
          <span className="text-[#991B1B] text-xs sm:text-sm">{negError}</span>
          <button
            data-testid="neg-enrich-retry"
            onClick={retryNeg}
            className="self-start inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0F172A] text-white text-xs font-semibold hover:bg-[#1E293B] transition-colors"
          >
            <Loader2 size={11} /> Retry
          </button>
        </div>
      ) : !local ? (
        <div className="flex items-center gap-2 text-[#64748B] text-xs sm:text-sm py-6">
          <Loader2 size={14} className="animate-spin" /> Generating your listing…
        </div>
      ) : (
      <>
      <div className="grid md:grid-cols-2 gap-3 mb-4 sm:mb-5">
        <div className="p-3 sm:p-4 rounded-xl bg-[#10B981]/[0.08] border border-[#10B981]/25">
          <div className="text-[10px] uppercase tracking-widest text-[#10B981] font-mono-num">Auto-Accept Threshold</div>
          <div className="mt-1 text-white text-xs sm:text-sm">
            Accept any offer <span className="font-mono-num font-bold text-base sm:text-lg text-[#10B981]">{money(local.auto_accept)}</span> or above
          </div>
        </div>
        <div className="p-3 sm:p-4 rounded-xl bg-[#6366F1]/[0.08] border border-[#6366F1]/25">
          <div className="text-[10px] uppercase tracking-widest text-[#8B8CF7] font-mono-num">Counter-Offer Strategy</div>
          <div className="mt-1 text-white text-xs sm:text-sm flex items-center gap-2 flex-wrap">
            Lowballed at <span className="font-mono-num font-bold text-white">{money(local.counter_from)}</span>?
            <ArrowRight size={12} className="text-white/40" />
            Counter with <span className="font-mono-num font-bold text-base sm:text-lg text-[#8B8CF7]">{money(local.counter_to)}</span>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-4 rounded-xl bg-black/40 border border-white/[0.06] flex items-start gap-3 flex-wrap sm:flex-nowrap">
        <div className="flex-1 min-w-0 w-full sm:w-auto">
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1 font-mono-num">One-Tap Reply</div>
          <p className="text-xs sm:text-sm text-white/85 italic">&ldquo;{local.quick_reply}&rdquo;</p>
        </div>
        <button
          data-testid="copy-reply-btn"
          onClick={copyReply}
          className="flex-shrink-0 px-3 py-2 rounded-full text-xs font-semibold bg-white/10 hover:bg-white/15 text-white flex items-center gap-1.5 transition-colors"
        >
          <Copy size={12} /> Copy Reply
        </button>
      </div>
      {negAdv && (
        <AdvancedStrip
          testid="negotiation-advanced"
          locked={!unlockedAdvanced}
          ctaTier="reseller"
          onUpgrade={onUpgrade}
          heading="Advanced · full negotiation script"
          ctaLabel="Full decline / counter / accept script with real $ thresholds for this item"
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-white/85">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#B91C1C]/80">Decline below</div>
              <div className="mt-0.5 font-mono-num text-base sm:text-lg font-bold text-white">{money(negAdv.decline_below_usd)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#F59E0B]/80">Counter range</div>
              <div className="mt-0.5 font-mono-num text-base sm:text-lg font-bold text-white">
                {money(negAdv.counter_range_lo_usd)}–{money(negAdv.counter_range_hi_usd)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#10B981]/80">Accept at</div>
              <div className="mt-0.5 font-mono-num text-base sm:text-lg font-bold text-white">{money(negAdv.accept_at_or_above_usd)}+</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/40">Stretch goal</div>
              <div className="mt-0.5 font-mono-num text-base sm:text-lg font-bold text-white">{money(negAdv.high_end_stretch_usd)}</div>
            </div>
          </div>
        </AdvancedStrip>
      )}
      </>
      )}
    </motion.div>
  );
}
